<script>
import ENUMS from '@shared/messages/enums.json';
import APICalendarEvents from '@app/services/API/CalendarEvents';
import isMeetingFinished, { MEETING_LEAD_MS, formatPlannedDuration } from '@shared/utils/meeting';
import moment from 'moment-timezone';

export default {
  i18n: {
    messages: {
      fr: {
        past: 'Passé',
        edit: 'Modifier',
        publish: 'Publier',
        set_draft: 'Mettre en brouillon',
        cancel_event: 'Annuler',
        delete: 'Supprimer',
        status_updated: 'Statut mis à jour',
        status_error: 'Erreur lors de la mise à jour',
        deleted: 'Événement supprimé',
        delete_error: 'Erreur lors de la suppression',
        delete_title: 'Cette action est définitive !',
        delete_message: '<strong>Attention</strong> ! Vous allez supprimer l\'événement <strong>{title}</strong>.',
        delete_warning: 'Cette action est définitive !',
        created_at: 'Créé le',
        visible_all: 'Tous les apprenants',
        visible_trainings: 'Apprenants de {count} formation | Apprenants de {count} formations',
        visible_meeting_participants: 'Participants de la classe virtuelle',
        manage_meeting: 'Gérer',
        join_meeting: 'Rejoindre',
        join_hint: 'Ce bouton sera disponible quelques minutes avant le début de la visio',
      },
    },
  },
  messages: {
    status: ENUMS.calendarEventStatus,
    statusClass: ENUMS.calendarEventStatusClass,
    frequency: ENUMS.calendarEventFrequency,
  },
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      now: Date.now(),
      nowTimer: null,
    };
  },
  mounted() {
    this.nowTimer = setInterval(() => {
      this.now = Date.now();
    }, 30000);
  },
  beforeDestroy() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer);
    }
  },
  computed: {
    dateLabel() {
      const tz = this.event.timezone || 'Europe/Paris';
      const start = moment.utc(this.event.start_date).tz(tz);

      if (this.isMultiDay) {
        const end = moment.utc(this.event.end_date).tz(tz);
        return `${start.format('ddd DD MMM YYYY, HH:mm')} → ${end.format('ddd DD MMM YYYY, HH:mm')}`;
      }

      if (this.event.is_all_day) {
        return start.format('ddd DD MMM YYYY');
      }

      return `${start.format('ddd DD MMM YYYY')} · ${start.format('HH:mm')}`;
    },
    isMultiDay() {
      if (!this.event.end_date) return false;
      const tz = this.event.timezone || 'Europe/Paris';
      const start = moment.utc(this.event.start_date).tz(tz);
      const end = moment.utc(this.event.end_date).tz(tz);
      return !start.isSame(end, 'day');
    },
    // Durée prévisionnelle (événement même jour, hors journée entière),
    // affichée à la place de l'heure de fin, comme sur les classes virtuelles.
    durationLabel() {
      if (this.event.is_all_day || !this.event.end_date || this.isMultiDay) {
        return null;
      }
      const minutes = moment.utc(this.event.end_date).diff(moment.utc(this.event.start_date), 'minutes');
      return formatPlannedDuration(minutes);
    },
    timezoneLabel() {
      const tz = this.event.timezone || 'Europe/Paris';
      // Offset à la date de l'événement (pas à aujourd'hui) : un événement
      // en juillet affiche UTC+2 pour Paris même consulté en hiver.
      const offset = moment.tz(this.event.start_date, tz).format('Z');
      return `${tz.replace(/_/g, ' ')} (UTC${offset})`;
    },
    locationLabel() {
      if (!this.event.location_type) return null;
      return this.event.location_value || null;
    },
    locationIcon() {
      const icons = {
        talkizy: 'video',
        visio: 'video',
        link: 'external-link-alt',
        address: 'map-marker-alt',
      };
      return icons[this.event.location_type] || null;
    },
    isRecurring() {
      return this.event.frequency && this.event.frequency !== 'once';
    },
    frequencyLabel() {
      return this.$options.messages.frequency[this.event.frequency];
    },
    trainingsCount() {
      return (this.event.trainings || []).length;
    },
    accessLabel() {
      if (this.trainingsCount === 0) {
        return this.$t('visible_all');
      }
      return this.$tc('visible_trainings', this.trainingsCount, { count: this.trainingsCount });
    },
    isPast() {
      if (!this.event.end_date) return false;
      return moment.utc(this.event.end_date).isBefore(moment.utc());
    },
    // Événement piloté par une classe virtuelle Talkizy : la classe est la
    // source de vérité (titre, dates, participants), l'événement n'est pas
    // éditable directement.
    isTalkizyMeeting() {
      return !!this.event.talkizy_meeting_id;
    },
    // Espace connecté à Talkizy. Une fois déconnecté, les events liés restent en
    // base mais deviennent orphelins : on masque « Gérer »/« Rejoindre » (qui
    // mèneraient à des pages injoignables).
    isTalkizyConnected() {
      return this.$store.getters['auth/isTalkizyConnected'];
    },
    isTalkizyMember() {
      return this.$store.getters['auth/isTalkizyMember'];
    },
    // État live de la visio Talkizy (status + vraies dates + lien), injecté par le
    // back dans le listing. Absent si Talkizy est injoignable.
    liveMeeting() {
      return this.isTalkizyMeeting ? this.event.talkizy_meeting : null;
    },
    // Objet au format attendu par le util partagé isMeetingFinished / canJoin.
    // On privilégie l'état live Talkizy (source de vérité, comme VisioCard) et on
    // retombe sur les dates de l'événement (UTC « Y-m-d H:i:s » → ISO) sinon.
    meetingSource() {
      if (this.liveMeeting) {
        return {
          status: this.liveMeeting.status,
          starts_at: this.liveMeeting.starts_at,
          ends_at: this.liveMeeting.ends_at,
        };
      }
      return {
        starts_at: this.event.start_date ? moment.utc(this.event.start_date).toISOString() : null,
        ends_at: this.event.end_date ? moment.utc(this.event.end_date).toISOString() : null,
      };
    },
    // Lien de visio : lien live Talkizy en priorité, sinon location_value (rempli côté back).
    joinUrl() {
      if (!this.isTalkizyMeeting) return null;
      return (this.liveMeeting && this.liveMeeting.join_url) || this.event.location_value;
    },
    isMeetingOver() {
      return isMeetingFinished(this.meetingSource, this.now);
    },
    // Logique identique à VisioCard : joignable dès quelques minutes avant le début,
    // active pendant la visio (status started), tant qu'elle n'est pas terminée.
    canJoin() {
      if (isMeetingFinished(this.meetingSource, this.now)) return false;
      if (this.meetingSource.status === 'started') return true;
      if (!this.meetingSource.starts_at) return false;
      return this.now >= new Date(this.meetingSource.starts_at).getTime() - MEETING_LEAD_MS;
    },
  },
  methods: {
    updateStatus(status) {
      if (this.event.status === status) return;

      const loader = this.$buefy.loading.open();
      APICalendarEvents.updateCalendarEvent(this.event.uuid, { status })
        .then(() => {
          this.$emit('update');
          this.$buefy.toast.open({
            message: this.$t('status_updated'),
            type: 'is-success',
          });
        })
        .catch(() => {
          this.$buefy.toast.open({
            message: this.$t('status_error'),
            type: 'is-danger',
          });
        })
        .finally(() => loader.close());
    },
    deleteItem() {
      this.$buefy.dialog.confirm({
        title: this.$t('delete_title'),
        message: `
          <div class="content">
            <p>${this.$t('delete_message', { title: this.event.title })}</p>
            <p class="has-text-danger has-text-weight-bold">${this.$t('delete_warning')}</p>
          </div>
        `,
        type: 'is-danger',
        focusOn: 'cancel',
        onConfirm: () => {
          const loader = this.$buefy.loading.open();
          APICalendarEvents.deleteCalendarEvent(this.event.uuid)
            .then(() => {
              this.$buefy.toast.open({
                message: this.$t('deleted'),
                type: 'is-success',
              });
              this.$emit('delete');
            })
            .catch(() => {
              this.$buefy.toast.open({
                message: this.$t('delete_error'),
                type: 'is-danger',
              });
            })
            .finally(() => loader.close());
        },
      });
    },
  },
};
</script>

<template>
  <div class="box pb-0 pr-0 overflow-hidden">
    <article class="media">
      <!-- Thumbnail -->
      <figure
        class="media-left image is-128x128 cursor-pointer bradius-8 is-hidden-mobile"
        :class="{ 'has-background-primary': !event.picture }"
        @click="$emit('view', event)"
      >
        <img
          v-if="event.picture"
          class="ofit-cover bradius-8"
          :style="{ width: '128px', height: '128px' }"
          :src="event.picture"
          :alt="event.title"
        >
        <span v-else class="flex-center-center h-full">
          <b-icon icon="calendar-alt" size="is-medium" custom-class="has-text-white" />
        </span>
      </figure>

      <!-- Content -->
      <div class="media-content mnw-0">
        <!-- Status + frequency tags -->
        <p class="is-size-8 mb-2">
          <span class="tag mr-2">
            <b-icon class="mr-1 is-size-9" icon="circle" :type="$options.messages.statusClass[event.status]" />
            {{ $options.messages.status[event.status] }}
          </span>
          <span v-if="isPast" class="tag is-outlined mr-2">
            {{ $t('past') }}
          </span>
          <span v-if="event.created_at">
            <time>{{ $t('created_at') }} <strong>{{ $moment.utc(event.created_at).local().format('DD/MM/YY') }}</strong></time>
          </span>
        </p>

        <!-- Title -->
        <h2 class="is-size-6 has-text-weight-bold mb-2">
          <a class="has-text-black" @click="$emit('view', event)">
            {{ event.title }}
          </a>
        </h2>

        <!-- Date + timezone -->
        <p class="is-size-8 has-text-grey-dark mb-1">
          <b-icon icon="clock" size="is-small" class="mr-1" />
          {{ dateLabel }}
          <span class="mx-1">·</span>
          <b-icon icon="globe" size="is-small" class="mr-1" />
          {{ timezoneLabel }}
        </p>

        <!-- Location -->
        <p v-if="locationLabel" class="is-size-8 has-text-grey-dark">
          <b-icon :icon="locationIcon" size="is-small" class="mr-1" />
          <span class="has-text-clipped is-inline-block valign-middle mw-260">{{ locationLabel }}</span>
        </p>
      </div>

      <!-- Actions -->
      <nav class="media-right buttons is-align-self-center">
          <b-button
            v-if="isTalkizyMeeting && isTalkizyConnected && isTalkizyMember"
            class="mr-4"
            icon-left="video"
            tag="router-link"
            :to="{ name: 'classe_virtuelle_meeting_settings', params: { meetingId: event.talkizy_meeting_id } }"
          >
            {{ $t('manage_meeting') }}
          </b-button>
          <template v-if="isTalkizyMeeting && isTalkizyConnected && isTalkizyMember && joinUrl && !isMeetingOver">
            <a
              v-if="canJoin"
              class="mr-4"
              :href="joinUrl"
              target="_blank"
              rel="noopener"
            >
              <b-button type="is-primary">
                {{ $t('join_meeting') }}
              </b-button>
            </a>
            <b-tooltip
              v-else
              class="mr-4"
              :label="$t('join_hint')"
              position="is-left"
              multilined
            >
              <b-button type="is-primary" disabled>
                {{ $t('join_meeting') }}
              </b-button>
            </b-tooltip>
          </template>
          <b-button v-if="!isTalkizyMeeting" class="mr-4" @click="$emit('edit', event)">
            {{ $t('edit') }}
          </b-button>
          <b-dropdown v-if="!isTalkizyMeeting" position="is-bottom-left" aria-role="menu" append-to-body>
            <template #trigger>
              <b-button type="is-text" icon-left="ellipsis-h" class="mr-4" />
            </template>
            <b-dropdown-item aria-role="menuitem" @click="updateStatus('PUBLISHED')">
              <b-icon icon="circle" type="is-success" class="is-size-9" />
              {{ $t('publish') }}
            </b-dropdown-item>
            <b-dropdown-item aria-role="menuitem" @click="updateStatus('DRAFT')">
              <b-icon icon="circle" type="is-warning" class="is-size-9" />
              {{ $t('set_draft') }}
            </b-dropdown-item>
            <b-dropdown-item aria-role="menuitem" @click="updateStatus('CANCELLED')">
              <b-icon icon="circle" type="is-danger" class="is-size-9" />
              {{ $t('cancel_event') }}
            </b-dropdown-item>
            <b-dropdown-item separator />
            <b-dropdown-item class="has-text-danger" aria-role="menuitem" @click="deleteItem">
              {{ $t('delete') }}
            </b-dropdown-item>
          </b-dropdown>
      </nav>
    </article>

    <!-- Footer stats -->
    <footer class="-mx-5 mt-5 px-5 py-3 btop-grey">
      <p class="has-text-grey-dark">
        <router-link
          v-if="isTalkizyMeeting && isTalkizyConnected && isTalkizyMember"
          class="is-inline-block is-size-8 has-text-grey-dark tdecoration-underline"
          :to="{ name: 'classe_virtuelle_meeting_learners', params: { meetingId: event.talkizy_meeting_id } }"
        >
          <b-icon icon="users" size="is-small" class="mr-1" />
          {{ $t('visible_meeting_participants') }}
        </router-link>
        <span v-else-if="isTalkizyMeeting" class="is-inline-block is-size-8">
          <b-icon icon="users" size="is-small" class="mr-1" />
          {{ $t('visible_meeting_participants') }}
        </span>
        <span v-else class="is-inline-block is-size-8">
          <b-icon icon="users" size="is-small" class="mr-1" />
          {{ accessLabel }}
        </span>
        <span v-if="isRecurring" class="is-inline-block is-size-8 ml-4">
          <b-icon icon="redo" size="is-small" class="mr-1" />
          {{ frequencyLabel }}
        </span>
        <span v-if="durationLabel" class="is-inline-block is-size-8 ml-4">
          <b-icon icon="hourglass-half" size="is-small" class="mr-1" />
          {{ durationLabel }}
        </span>
      </p>
    </footer>
  </div>
</template>
